import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Package, Truck, ArrowRight, Storefront } from "@phosphor-icons/react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { LoadCard } from "@/components/LoadCard";
import { PostLoadDialog } from "@/components/PostLoadDialog";
import { RequestLoadDialog } from "@/components/RequestLoadDialog";
import { LeadDialog } from "@/components/LeadDialog";
import { getLoads } from "@/lib/api";

export default function Portal() {
  const [tab, setTab] = useState("shipper");
  const [loads, setLoads] = useState([]);
  const [postOpen, setPostOpen] = useState(false);
  const [reqLoad, setReqLoad] = useState(null);
  const [quoteOpen, setQuoteOpen] = useState(false);

  const load = useCallback(() => getLoads({ sort: "newest" }).then(setLoads).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-[#0B1220] text-white">
      <Navbar onQuote={() => setQuoteOpen(true)} />

      <div className="pt-28 pb-8 border-b border-white/10 bg-[#111A2E] relative overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-30" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-[#2563EB] uppercase text-xs tracking-[0.2em] mb-2">Portal</div>
          <h1 className="font-display text-3xl lg:text-5xl font-bold tracking-tight mb-3">Post loads. Book freight.</h1>
          <p className="text-[#94A3B8] max-w-xl">Shippers post loads in seconds. Carriers browse and request them instantly. Accounts & dispatch dashboards arrive in Phase 2.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        <div className="inline-flex bg-[#111A2E] border border-white/10 rounded-full p-1 mb-10">
          <button onClick={() => setTab("shipper")} data-testid="portal-tab-shipper" className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-colors flex items-center gap-2 ${tab === "shipper" ? "bg-[#2563EB] text-white" : "text-[#94A3B8] hover:text-white"}`}>
            <Storefront size={18} /> I'm a Shipper
          </button>
          <button onClick={() => setTab("carrier")} data-testid="portal-tab-carrier" className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-colors flex items-center gap-2 ${tab === "carrier" ? "bg-[#2563EB] text-white" : "text-[#94A3B8] hover:text-white"}`}>
            <Truck size={18} /> I'm a Carrier
          </button>
        </div>

        {tab === "shipper" ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            <div className="bg-[#111A2E] border border-white/10 rounded-2xl p-10">
              <div className="w-14 h-14 rounded-xl bg-[#2563EB] flex items-center justify-center mb-6"><Package size={28} weight="bold" className="text-white" /></div>
              <h2 className="font-display text-2xl font-bold tracking-tight mb-3">Post a load to the board</h2>
              <p className="text-[#94A3B8] mb-8">Enter your lane, equipment, and rate. Your load goes live immediately and Coastal dispatch is notified.</p>
              <button onClick={() => setPostOpen(true)} data-testid="portal-post-load-btn" className="px-7 py-3.5 rounded-full bg-[#2563EB] text-white font-semibold hover:bg-[#1D4ED8] transition-colors flex items-center gap-2">
                <Package size={20} /> Post a Load
              </button>
            </div>
            <div className="bg-[#111A2E] border border-white/10 rounded-2xl p-10">
              <h3 className="font-display text-lg font-bold mb-4">Recently posted</h3>
              <div className="space-y-3">
                {loads.slice(0, 4).map((l) => (
                  <div key={l.id} className="flex items-center justify-between text-sm border-b border-white/5 pb-3 last:border-0">
                    <span className="text-[#cbd5e1]">{l.origin_city}, {l.origin_state} → {l.dest_city}, {l.dest_state}</span>
                    <span className="text-[#10B981] font-bold">${l.rate_usd.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl font-bold tracking-tight">Browse available loads</h2>
              <Link to="/loads" data-testid="portal-view-board-btn" className="text-[#2563EB] hover:underline flex items-center gap-1 text-sm font-semibold">Full board <ArrowRight size={16} /></Link>
            </div>
            <div className="space-y-4">
              {loads.slice(0, 6).map((l, i) => <LoadCard key={l.id} load={l} index={i} onRequest={setReqLoad} />)}
            </div>
          </div>
        )}
      </div>

      <Footer />
      <PostLoadDialog open={postOpen} onOpenChange={setPostOpen} onPosted={load} />
      <RequestLoadDialog open={!!reqLoad} onOpenChange={(v) => !v && setReqLoad(null)} load={reqLoad} />
      <LeadDialog open={quoteOpen} onOpenChange={setQuoteOpen} type="quote" />
    </div>
  );
}
